
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useState, ReactNode } from "react";
import { User, WalletType, Role } from "../types";
import { mockLogin, mockWalletLogin, mockRegister } from "../services/mock";

interface AuthContextProps {
  user?: User;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, role: Role) => Promise<void>;
  loginWithWallet: (type: WalletType) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
        const loggedInUser = await mockLogin(email, password);
        setUser(loggedInUser);
    } catch(error) {
        console.error("Login failed:", error);
    } finally {
        setIsLoading(false);
    }
  };

  const register = async (email: string, password: string, name: string, role: Role) => {
      setIsLoading(true);
      try {
          const registeredUser = await mockRegister(email, password, name, role);
          setUser(registeredUser);
      } catch (error) {
          console.error("Registration failed:", error);
      } finally {
          setIsLoading(false);
      }
  };

  const loginWithWallet = async (type: WalletType) => {
    setIsLoading(true);
    try {
        const loggedInUser = await mockWalletLogin(type);
        setUser(loggedInUser);
    } catch(error) {
        console.error("Wallet login failed:", error);
    } finally {
        setIsLoading(false);
    }
  }

  const logout = () => {
    setUser(undefined);
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, register, loginWithWallet, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
